/**
 * Categories on the client side are config-item categories (the values on `config_item.category`,
 * which the Discovery Grid's guided blocks and the home's category rows carry as they are). Nothing
 * here guesses: a screen that is handed a category opens exactly that category's items; a hint
 * from elsewhere (the recommendation engine's canonical keys, the older twelve-slug menu) is
 * resolved through this alias table to a category the company actually has, or to nothing.
 */
import { formatCategoryLabel } from './normalizeAcronyms';

/** Canonical client-side category → the spellings and engine keys that mean it. */
export const CATEGORY_ALIASES: Record<string, string[]> = {
  assets: ['asset', 'assets', 'cmdb', 'asset-management', 'asset_management'],
  incidents: ['incident', 'incidents', 'incident-management', 'incident_management', 'incident_operating_model', 'incident_workflow', 'incident_priority', 'incident_sla', 'incident_queues', 'incident_request_types', 'incident_locked'],
  service_requests: ['service_requests', 'service-requests', 'service-request', 'service_request', 'srm', 'request-type-group', 'request_types', 'srm_workflow', 'srm_priority', 'srm_sla', 'srm_channels', 'srm_queues', 'srm_request_types', 'srm_locked', 'service_catalog'],
  problems: ['problem', 'problems', 'problem-management', 'problem_management', 'problem_enablement', 'rca_policy', 'known_error_db', 'problem_request_types', 'problem_locked'],
  changes: ['change', 'changes', 'change-management', 'change_management', 'change_models', 'risk_model', 'calendar_blackouts', 'change_request_types', 'change_locked'],
  slas: ['sla', 'slas', 'sla_calendar'],
  queues: ['queue', 'queues', 'queue_config'],
  automation: ['automation', 'automations', 'automation-rules', 'automation_rules'],
  permissions: ['permission', 'permissions', 'team-settings', 'team_settings', 'security', 'global_teams', 'global_roles', 'global_users'],
  integrations: ['integration', 'integrations'],
  workflows: ['workflow', 'workflows'],
  knowledge: ['knowledge', 'knowledge-base', 'knowledge_base', 'kb'],
  dashboards: ['dashboard', 'dashboards', 'dashboards_enablement', 'dashboards_definitions'],
  approvals: ['approval', 'approvals'],
  portal: ['portal', 'portals'],
  notifications: ['notification', 'notifications', 'email'],
};

/** Friendly names for the canonical categories; anything else is title-cased from its key. */
const CATEGORY_NAMES: Record<string, string> = {
  assets: 'Asset Management',
  incidents: 'Incident Management',
  service_requests: 'Service Request Management',
  problems: 'Problem Management',
  changes: 'Change Management',
  slas: 'SLA Management',
  queues: 'Queues & Routing',
  automation: 'Automation Rules',
  permissions: 'Permissions & Security',
  integrations: 'Integrations',
  workflows: 'Workflows',
  knowledge: 'Knowledge Management',
};

const norm = (v: unknown) => String(v ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

/** The canonical category a value belongs to (itself when it is one), or null. */
export function canonicalCategory(value: unknown): string | null {
  const key = norm(value);
  if (!key) return null;
  if (CATEGORY_ALIASES[key]) return key;
  for (const [canonical, aliases] of Object.entries(CATEGORY_ALIASES)) {
    if (aliases.some((a) => norm(a) === key)) return canonical;
  }
  return null;
}

/** True when the item's category is the category, by spelling or by alias. */
export function itemInCategory(item: { category?: string | null }, category: string): boolean {
  const own = norm(item?.category);
  if (!own) return false;
  const want = norm(category);
  if (own === want) return true;
  const canonical = canonicalCategory(want);
  return canonical !== null && canonicalCategory(own) === canonical;
}

/** The company's items in a category: the exact spelling first; the aliases only when that finds nothing. */
export function configItemsForCategory<T extends { category?: string | null }>(items: T[], category: string): T[] {
  const want = norm(category);
  const exact = items.filter((it) => norm(it?.category) === want);
  if (exact.length > 0) return exact;
  return items.filter((it) => itemInCategory(it, category));
}

/**
 * A category the company actually has that a hint (an engine key, an old slug, a config-item
 * category) points at, spelled as the items spell it; null when nothing matches, so the caller
 * can send the client to the home instead of guessing.
 */
export function resolveCategory(items: Array<{ category?: string | null }>, hint: unknown): string | null {
  const key = norm(hint);
  if (!key) return null;
  const exact = items.find((it) => norm(it?.category) === key);
  if (exact?.category) return String(exact.category);
  const canonical = canonicalCategory(key);
  if (!canonical) return null;
  const hit = items.find((it) => canonicalCategory(it?.category) === canonical);
  return hit?.category ? String(hit.category) : null;
}

export function categoryDisplayName(category: string): string {
  const canonical = canonicalCategory(category);
  if (canonical && CATEGORY_NAMES[canonical]) return CATEGORY_NAMES[canonical];
  return formatCategoryLabel(String(category).replace(/-/g, '_')) || category;
}
